import { useQuery } from '@tanstack/react-query';
import AdminServerActionCreators from 'actions/AdminServerActionCreators';
import { useFireStoreSubscription } from '../../../../hooks/useFireStoreSubscription';
import { useEffect, useState, useReducer, useCallback, useMemo } from 'react';
import { differenceInMinutes } from 'date-fns';
import ShipmentWebAPIUtils, { getShipmentModifiedDate, getShipmentWithMapping, } from 'utils/ShipmentWebAPIUtils';
const POLLING_INTERVAL = 60000;
function fetchShipmentModifiedDate({ queryKey }) {
    const [, shipmentId] = queryKey;
    return getShipmentModifiedDate(shipmentId);
}
function fetchShipment({ queryKey }) {
    const [, shipmentId, queryOptions] = queryKey;
    return getShipmentWithMapping(shipmentId, queryOptions);
}
export function useShipment({ shipmentId, enabled, options }) {
    const queryOptions = options !== null && options !== void 0 ? options : {};
    const { data, isLoading: loading, error, isError, refetch, } = useQuery({
        queryKey: ['getShipment', shipmentId, queryOptions],
        queryFn: fetchShipment,
        refetchInterval: POLLING_INTERVAL,
        refetchIntervalInBackground: true,
        enabled,
        refetchOnWindowFocus: true,
    });
    return { error, isError, loading, data, refetch };
}
export function useShipmentModifiedDate({ shipmentId }) {
    var _a, _b, _c, _d;
    const { data: res, loading, error, } = useQuery({
        queryKey: ['getShipmentModifiedDate', shipmentId],
        queryFn: fetchShipmentModifiedDate,
        refetchInterval: POLLING_INTERVAL,
        refetchIntervalInBackground: true,
        enabled: true,
        refetchOnWindowFocus: true,
    });
    if ((_b = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.item) === null || _b === void 0 ? void 0 : _b.modified_at) {
        return { data: (_d = (_c = res.data) === null || _c === void 0 ? void 0 : _c.item) === null || _d === void 0 ? void 0 : _d.modified_at, loading, error };
    }
    return { data: undefined, loading, error };
}
export function useShipmentSubscription({ shipmentId }) {
    const [shipment, setShipment] = useState();
    // Firestore subscription
    const { snapshot, loading: firestoreLoading, error: firestoreError, } = useFireStoreSubscription({
        path: `shipments/${shipmentId}`,
    });
    // Modified date query to check for stale data
    const { data: modifiedDate } = useShipmentModifiedDate({ shipmentId });
    const needsRefresh = !!firestoreError || (snapshot === null || snapshot === void 0 ? void 0 : snapshot.exists()) === false;
    // Shipment data query for fallback and refresh
    const { data: polledShipment, loading: polledShipmentLoading, error: polledShipmentError, refetch, } = useShipment({
        shipmentId,
        enabled: needsRefresh,
        options: needsRefresh ? { refresh: true } : undefined,
    });
    /**
     * @typedef {Object} ShipmentState
     * @property {Object|null} shipment - The current shipment data
     * @property {'firestore'|'poll'|null} dataSource - Source of the current data
     */
    /**
     * Reducer to manage shipment state updates
     * @param {ShipmentState} state - Current state
     * @param {Object} action - Action to perform
     */
    const shipmentReducer = (state, action) => {
        switch (action.type) {
            case 'SET_FIRESTORE_DATA':
                return {
                    shipment: action.payload,
                    dataSource: 'firestore',
                };
            case 'SET_POLLED_DATA':
                return {
                    shipment: action.payload,
                    dataSource: 'poll',
                };
            default:
                return state;
        }
    };
    const [{ shipment: localShipment, dataSource }, dispatch] = useReducer(shipmentReducer, {
        shipment: null,
        dataSource: null,
    });
    /**
     * Handle Firestore data updates
     * @param {Object} data - Firestore snapshot data
     */
    const handleFirestoreData = useCallback((data) => {
        if (data) {
            dispatch({ type: 'SET_FIRESTORE_DATA', payload: data });
            safeDispatchSuccess(data);
        }
    }, [safeDispatchSuccess]);
    /**
     * Handle polled data updates
     * @param {Object} data - Polled shipment data
     */
    const handlePolledData = useCallback((data) => {
        if (data) {
            dispatch({ type: 'SET_POLLED_DATA', payload: data });
            safeDispatchSuccess(data);
        }
    }, [safeDispatchSuccess]);
    useEffect(() => {
        const abortController = new AbortController();
        // Firestore has priority - if it has data, use it
        if (snapshot === null || snapshot === void 0 ? void 0 : snapshot.exists()) {
            const data = snapshot.data();
            if (!abortController.signal.aborted) {
                handleFirestoreData(data);
            }
        }
        // Only use polled data if Firestore doesn't have data
        else if (polledShipment && !(snapshot === null || snapshot === void 0 ? void 0 : snapshot.exists())) {
            if (!abortController.signal.aborted) {
                handlePolledData(polledShipment);
            }
        }
        return () => {
            abortController.abort();
        };
    }, [
        polledShipment,
        snapshot,
        handleFirestoreData,
        handlePolledData,
        shipmentId,
    ]);
    // Update external shipment state
    useEffect(() => {
        setShipment(localShipment);
    }, [localShipment]);
    useEffect(() => {
        if (!shipment || !modifiedDate || !shipment.modified_at) {
            return;
        }
        if (differenceInMinutes(modifiedDate, shipment.modified_at) >= 1) {
            ShipmentWebAPIUtils.getShipmentV2(shipmentId, { refresh: true });
        }
    }, [modifiedDate, shipment, shipmentId]);
    /**
     * Safely dispatch success action with error boundary
     */
    const safeDispatchSuccess = useCallback((data) => {
        try {
            AdminServerActionCreators.receiveShipmentSuccess({
                job: data,
            });
        }
        catch (e) {
            console.error('Error dispatching shipment success:', e);
        }
    }, []);
    // Memoize loading state calculation
    const loading = useMemo(() => (polledShipmentLoading && (!(snapshot === null || snapshot === void 0 ? void 0 : snapshot.exists()) || firestoreError)) ||
        firestoreLoading, [polledShipmentLoading, snapshot, firestoreError, firestoreLoading]);
    // Memoize error state
    const error = useMemo(() => polledShipmentError || firestoreError, [polledShipmentError, firestoreError]);
    // Add retry capability for Firestore errors
    useEffect(() => {
        if (firestoreError && !polledShipment) {
            const retryTimeout = setTimeout(() => {
                refetch();
            }, 5000); // Retry after 5 seconds
            return () => clearTimeout(retryTimeout);
        }
    }, [firestoreError, polledShipment, refetch]);
    /**
     * @typedef {Object} ShipmentSubscriptionResult
     * @property {Object|null} shipment - The current shipment data
     * @property {boolean} loading - Whether the data is currently loading
     * @property {Error|null} error - Any error that occurred during data fetching
     * @property {Function} refetch - Function to manually refetch the data
     */
    return {
        shipment,
        loading,
        error,
        refetch,
        dataSource,
    };
}
